import type { CSSProperties, KeyboardEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/button';
import type { AsyncSelectorMode } from './AsyncSelector.types';
import type { ColumnDef } from '../types';
import type { UsePagedAsyncSuggestionsResult } from '../search/usePagedAsyncSuggestions';
import {
  SelectorCheckIcon,
  SelectorEditIcon,
  SelectorKebabIcon,
  SelectorPlusIcon,
  SelectorSearchIcon,
} from './selectorIcons';

/** Okno čísel stránek kolem aktuální stránky (listpage idiom: ±2 + výpustka). */
const PAGER_WINDOW = 2;

export interface AsyncSelectorPopoverProps<TItem> {
  mode: AsyncSelectorMode;
  popupId: string;
  columns: readonly ColumnDef<TItem>[];
  suggestions: UsePagedAsyncSuggestionsResult<TItem>;
  getKey: (item: TItem) => string | number;
  isSelected: (item: TItem) => boolean;
  /** Zamčený řádek (button mode, už v cílovém seznamu) — zaškrtnutý, ztlumený, neklikatelný. */
  isLocked?: (item: TItem) => boolean;
  selectedCount: number;
  activeIdx: number;
  onHoverRow: (index: number) => void;
  onPickRow: (item: TItem) => void;
  searchRef: RefObject<HTMLInputElement | null>;
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  searchAriaLabel: string;
  /** Proklik do detailu našeptávané položky — totožné chování jako ✎ na vybraném řádku pole. */
  onEditItem?: (item: TItem) => void;
  editLabel: string;
  editDisabled?: boolean;
  // ---- viewport-aware umístění (z orchestrátoru) ----
  popRef?: RefObject<HTMLDivElement | null>;
  vpos?: 'down' | 'up';
  halign?: 'left' | 'right';
  /** Max. výška scrollovaného seznamu (px) dle dostupného místa. */
  listMaxHeight?: number;
  // ---- režim tlačítka (trigger="button") ----
  isButton?: boolean;
  /** + Nový záznam v našeptávači (jen button mode). */
  onCreate?: () => void;
  /** ⋯ Otevřít seznam v našeptávači (jen button mode; před otevřením modalu zavře popover). */
  onOpenList?: () => void;
  createLabel?: string;
  openListLabel?: string;
  /** Patička s potvrzením draftu (button + multi). */
  showConfirm?: boolean;
  confirmLabel?: string;
  confirmCount?: number;
  onConfirm?: () => void;
}

function headColStyle<TItem>(col: ColumnDef<TItem>): CSSProperties {
  return { flex: col.flex ?? 1, textAlign: col.align ?? 'left' };
}

function cellStyle<TItem>(col: ColumnDef<TItem>): CSSProperties {
  return {
    flex: col.flex ?? 1,
    textAlign: col.align ?? 'left',
    color: col.muted ? 'var(--phx-text-muted)' : undefined,
    fontFamily: col.mono ? 'var(--phx-font-mono)' : undefined,
    fontVariantNumeric: col.numeric ? 'tabular-nums' : undefined,
  };
}

/**
 * Popover selectoru dle designového handoffu: vyhledávání (autofokus),
 * záhlaví sloupců, stránkovaný seznam záznamů (multi s checkboxy) a patička
 * s počtem záznamů + stránkováním v listpage idiomu.
 */
export function AsyncSelectorPopover<TItem>({
  mode,
  popupId,
  columns,
  suggestions,
  getKey,
  isSelected,
  isLocked,
  selectedCount,
  activeIdx,
  onHoverRow,
  onPickRow,
  searchRef,
  onSearchKeyDown,
  placeholder,
  searchAriaLabel,
  onEditItem,
  editLabel,
  editDisabled,
  popRef,
  vpos = 'down',
  halign = 'left',
  listMaxHeight,
  isButton,
  onCreate,
  onOpenList,
  createLabel,
  openListLabel,
  showConfirm,
  confirmLabel,
  confirmCount = 0,
  onConfirm,
}: AsyncSelectorPopoverProps<TItem>) {
  const { t } = useTranslation('asyncSelector');
  const multi = mode === 'multi';
  const totalPages = Math.max(1, Math.ceil(suggestions.totalCount / suggestions.pageSize));
  const page = suggestions.page;
  const goTo = (next: number) => suggestions.setPage(Math.min(totalPages, Math.max(1, next)));

  const pageNumbers: number[] = [];
  for (
    let p = Math.max(1, page - PAGER_WINDOW);
    p <= Math.min(totalPages, page + PAGER_WINDOW);
    p++
  ) {
    pageNumbers.push(p);
  }

  return (
    <div
      className="phxsel-pop"
      ref={popRef}
      data-testid="async-selector-popup"
      data-vpos={vpos}
      data-halign={halign}
    >
      <div className="phxsel-search">
        <span className="phxsel-search-ico">
          <SelectorSearchIcon />
        </span>
        <input
          ref={searchRef}
          value={suggestions.query}
          onChange={(event) => suggestions.setQuery(event.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={placeholder}
          aria-label={searchAriaLabel}
        />
        {isButton && (onCreate || onOpenList) ? (
          // V režimu tlačítka není boční lišta — akce +/⋯ stojí vpravo v našeptávači.
          <span className="phxsel-search-acts">
            {onCreate ? (
              <button
                type="button"
                className="phxsel-search-act"
                title={createLabel}
                aria-label={createLabel}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  onCreate();
                }}
              >
                <SelectorPlusIcon size={13} />
              </button>
            ) : null}
            {onOpenList ? (
              <button
                type="button"
                className="phxsel-search-act"
                title={openListLabel}
                aria-label={openListLabel}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenList();
                }}
              >
                <SelectorKebabIcon size={13} />
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      <div className="phxsel-head">
        {multi ? <span className="phxsel-check" data-head="" /> : null}
        {columns.map((col) => (
          <span key={col.id} className="phxsel-col" style={headColStyle(col)}>
            {col.header}
          </span>
        ))}
        {onEditItem ? (
          // spacer zarovnaný se sloupcem ✎ (22px tlačítko − 10px záporný margin)
          <span className="phxsel-col" style={{ flex: '0 0 12px' }} aria-hidden />
        ) : null}
      </div>

      <div
        className="phxsel-list"
        id={popupId}
        role="listbox"
        aria-multiselectable={multi}
        style={listMaxHeight != null ? { maxHeight: listMaxHeight } : undefined}
      >
        {suggestions.isLoading ? (
          <div className="phxsel-empty" data-testid="async-selector-loading">
            {t('table.loading')}
          </div>
        ) : suggestions.isError ? (
          <div className="phxsel-empty text-phx-danger" data-testid="async-selector-error">
            {t('table.error')}
          </div>
        ) : suggestions.items.length === 0 ? (
          <div className="phxsel-empty" data-testid="async-selector-empty">
            {suggestions.query ? t('noMatch', { query: suggestions.query }) : t('table.empty')}
          </div>
        ) : (
          suggestions.items.map((item, index) => {
            const locked = isLocked?.(item) ?? false;
            const selected = locked || isSelected(item);
            return (
              <div
                key={getKey(item)}
                className="phxsel-row"
                role="option"
                aria-selected={selected}
                aria-disabled={locked || undefined}
                data-testid="async-selector-option"
                {...(selected ? { 'data-selected': '' } : {})}
                {...(locked ? { 'data-locked': '' } : {})}
                {...(index === activeIdx ? { 'data-active': '' } : {})}
                onMouseDown={(event) => {
                  event.preventDefault();
                  if (!locked) onPickRow(item);
                }}
                onMouseEnter={() => onHoverRow(index)}
              >
                {multi ? (
                  <span className="phxsel-check">{selected ? <SelectorCheckIcon /> : null}</span>
                ) : null}
                {columns.map((col) => (
                  <span key={col.id} className="phxsel-cell" style={cellStyle(col)}>
                    {col.cell(item)}
                  </span>
                ))}
                {onEditItem ? (
                  <button
                    type="button"
                    className="phxsel-rowbtn"
                    title={editLabel}
                    aria-label={editLabel}
                    tabIndex={-1}
                    disabled={editDisabled}
                    onMouseDown={(event) => {
                      // nepustit mousedown na řádek (výběr) a nechat fokus ve vyhledávání
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onEditItem(item);
                    }}
                  >
                    <SelectorEditIcon />
                  </button>
                ) : null}
              </div>
            );
          })
        )}
      </div>

      <div className="phxsel-foot" data-testid="async-selector-foot">
        <span className="phxsel-foot-info">
          {t('footer.totalItems', { count: suggestions.totalCount })}
          {/* Počet vybraných u pole; v button módu stojí u tlačítka „Vybrat" v patičce. */}
          {multi && !isButton && selectedCount > 0 ? (
            <>
              {' · '}
              <b>{t('footer.selected', { count: selectedCount })}</b>
            </>
          ) : null}
        </span>
        {totalPages > 1 ? (
          <span className="phxsel-pager">
            <Button
              size="sm"
              disabled={page === 1}
              onClick={() => goTo(1)}
              aria-label={t('footer.firstPage')}
            >
              «
            </Button>
            <Button size="sm" disabled={page === 1} onClick={() => goTo(page - 1)}>
              ‹ {t('footer.previous')}
            </Button>
            {pageNumbers[0]! > 1 ? <span className="phxsel-pager-ellipsis">…</span> : null}
            {pageNumbers.map((p) => (
              <Button key={p} size="sm" active={p === page} onClick={() => goTo(p)}>
                {p}
              </Button>
            ))}
            {pageNumbers[pageNumbers.length - 1]! < totalPages ? (
              <span className="phxsel-pager-ellipsis">…</span>
            ) : null}
            <Button size="sm" disabled={page === totalPages} onClick={() => goTo(page + 1)}>
              {t('footer.next')} ›
            </Button>
            <Button
              size="sm"
              disabled={page === totalPages}
              onClick={() => goTo(totalPages)}
              aria-label={t('footer.lastPage')}
            >
              »
            </Button>
          </span>
        ) : null}
        {showConfirm ? (
          // Patička button+multi: počet vybraných v draftu + potvrzovací „Vybrat".
          <span className="phxsel-foot-confirm">
            <span className="phxsel-foot-selcount">
              <b>{confirmCount}</b> {t('footer.selectedSuffix', { count: confirmCount })}
            </span>
            <Button
              variant="primary"
              size="sm"
              disabled={confirmCount === 0}
              onMouseDown={(event) => event.preventDefault()}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </span>
        ) : null}
      </div>
    </div>
  );
}
